@file:OptIn(ExperimentalWasmJsInterop::class)

package com.mrpowergamerbr.puppydough.webrune

import com.mrpowergamerbr.puppydough.DownloadUtils
import com.mrpowergamerbr.puppydough.MD5
import com.mrpowergamerbr.puppydough.VirtualGamepad
import com.mrpowergamerbr.puppydough.OPFSUtils
import com.mrpowergamerbr.puppydough.WADUtils
import com.mrpowergamerbr.puppydough.clickToStart
import com.mrpowergamerbr.puppydough.el
import com.mrpowergamerbr.puppydough.processingGame
import com.mrpowergamerbr.puppydough.showGameCanvas
import com.mrpowergamerbr.puppydough.webrune.UIState.*
import com.mrpowergamerbr.puppydough.webrune.packets.Boxed2PlayPacket
import js.buffer.toByteArray
import js.coroutines.promise
import js.iterable.asFlow
import js.iterable.iterator
import jszip.*
import kotlinx.coroutines.GlobalScope
import kotlinx.coroutines.launch
import kotlinx.serialization.json.Json
import web.animations.requestAnimationFrame
import web.audio.AudioContext
import web.audio.AudioContextState
import web.audio.running
import web.blob.arrayBuffer
import web.blob.byteArray
import web.cssom.ClassName
import web.dom.*
import web.events.addEventListener
import web.events.addHandler
import web.file.File
import web.file.FileList
import web.fs.*
import web.html.*
import web.location.location
import web.messaging.MESSAGE
import web.messaging.MessageEvent
import web.navigator.navigator
import web.prompts.alert
import web.prompts.confirm
import web.storage.getDirectory
import web.storage.persist
import web.storage.sessionStorage
import web.streams.close
import web.url.URL
import web.url.URLSearchParams
import web.window.window
import kotlin.uuid.Uuid

fun ParentNode.compatibilityIcon(compatibility: GameCompatibility) = el(HtmlTagName.span) {
    className = ClassName("compatibility-icon ${compatibility.name.lowercase()}")
    title = when (compatibility) {
        GameCompatibility.COMPATIBLE -> "Compatible"
        GameCompatibility.UNKNOWN -> "Unknown version, it may not work correctly"
        GameCompatibility.INCOMPATIBLE -> "Incompatible version"
    }
}

private suspend fun collectPreloadEntries(
    directory: FileSystemDirectoryHandle,
    gameFolder: String,
    urlPrefix: String,
    entries: MutableList<PreloadEntry>,
    skipDirectory: (String) -> Boolean
): Map<String, String> {
    // I hate the Wasm runner because it always tries loading it in lowercase
    val casingMappings = mutableMapOf<String, String>()

    directory.values().asFlow().collect {
        if (it is FileSystemFileHandle) {
            // data.win is loaded separately via game.unx, and options.ini is already created by the runner itself (it throws EEXIST if we preload it)
            if (gameFolder.isEmpty() && (it.name in WADUtils.WAD_FILENAMES || it.name == "options.ini"))
                return@collect
            val lowercaseFileName = it.name.lowercase()

            casingMappings[lowercaseFileName] = it.name
            println("$lowercaseFileName -> ${it.name}")
            entries.add(PreloadEntry(if (gameFolder.isEmpty()) "assets/" else "assets/$gameFolder", lowercaseFileName, "$urlPrefix$lowercaseFileName"))
        } else if (it is FileSystemDirectoryHandle) {
            if (skipDirectory(it.name))
                return@collect

            casingMappings += collectPreloadEntries(
                it,
                if (gameFolder.isEmpty()) it.name else "$gameFolder/${it.name}",
                "$urlPrefix${it.name}/",
                entries,
                skipDirectory
            )
        }
    }
    return casingMappings
}

var state: UIState = UploadGame
    set(value) { field = value; renderFromScratch() }   // every state change re-renders

val container
    get() = document.getElementById(ElementId("container"))!!

var config = WebruneConfig(listOf(), null)
    private set

fun renderFromScratch() {
    container.replaceChildren()
    container.render()
}

suspend fun detectGame(inputFiles: FileList) {
    val files = mutableListOf<File>()

    for (file in inputFiles) {
        files.add(file)
    }

    val gameWADFile = files.firstOrNull { it.webkitRelativePath.substringAfter("/") in WADUtils.WAD_FILENAMES }
    if (gameWADFile == null) {
        alert("Couldn't find a Game WAD inside the game folder! Please make sure the game is in the root folder and has a data.win file inside it.")
        return
    }

    val dataWinArrayBuffer = gameWADFile.arrayBuffer()
    val dataWinBytes = dataWinArrayBuffer.toByteArray()
    val hash = MD5.hashHex(dataWinBytes)

    state = DetectedGame(gameWADFile, files, hash)
}

fun ParentNode.render() {
    when (val s = state) {
        is Ready -> {
            el(HtmlTagName.div) {
                className = ClassName("menu")

                el(HtmlTagName.button).apply {
                    className = ClassName("undertale-button")

                    el(HtmlTagName.div) {
                        textContent = "Start Game"
                    }

                    this.clickEvent.addHandler {
                        sessionStorage.setItem("startedFromMainPage", "true")
                        location.href = "/play?id=${s.version.id}"
                    }
                }

                // The native file input can't be styled, so it is hidden inside a label that acts as the button
                el(HtmlTagName.label) {
                    className = ClassName("undertale-button")

                    el(HtmlTagName.div) {
                        textContent = "Import Saves"
                    }

                    el(HtmlTagName.input).apply {
                        this.type = InputType.file
                        this.accept = ".zip"

                        this.changeEvent.addHandler {
                            val file = this.files?.item(0) ?: return@addHandler
                            this.disabled = true

                            GlobalScope.launch {
                                OPFSUtils.extractZIPToFolder("versions/${s.version.id}/save/", JSZip().load(file.arrayBuffer()))
                                state = Ready(s.version)
                            }
                        }
                    }
                }

                el(HtmlTagName.button) {
                    className = ClassName("undertale-button")

                    el(HtmlTagName.div) {
                        textContent = "Export Saves"
                    }

                    this.clickEvent.addHandler {
                        this.disabled = true

                        GlobalScope.launch {
                            try {
                                DownloadUtils.download(OPFSUtils.compressFolderToZIP("versions/${s.version.id}/save/"))
                            } finally {
                                this@el.disabled = false
                            }
                        }
                    }
                }

                el(HtmlTagName.button) {
                    className = ClassName("undertale-button")

                    el(HtmlTagName.div) {
                        textContent = "Delete Saves"
                    }

                    this.clickEvent.addHandler {
                        val result = confirm("Are you really sure?")

                        if (result) {
                            this.disabled = true

                            GlobalScope.launch {
                                val directory = navigator.storage.getDirectory()
                                try {
                                    directory.getDirectoryHandle("versions", FileSystemGetDirectoryOptions(create = true)).getDirectoryHandle(s.version.id, FileSystemGetDirectoryOptions(create = true)).removeEntry("save", FileSystemRemoveOptions(recursive = true))
                                } catch (e: Throwable) {
                                }
                                state = Ready(s.version)
                            }
                        }
                    }
                }

                el(HtmlTagName.button) {
                    className = ClassName("undertale-button")

                    el(HtmlTagName.div) {
                        textContent = "Delete Game Data"
                    }

                    this.clickEvent.addHandler {
                        val result = confirm("Are you really sure?")

                        if (result) {
                            this.disabled = true

                            GlobalScope.launch {
                                try {
                                    navigator.storage.getDirectory().getDirectoryHandle("versions", FileSystemGetDirectoryOptions(create = true)).removeEntry(s.version.id, FileSystemRemoveOptions(recursive = true))
                                } catch (e: Throwable) {
                                }

                                updateConfig { it.copy(versions = it.versions - s.version, activeVersion = null) }

                                state = if (config.versions.isNotEmpty()) {
                                    SelectGameVersion
                                } else {
                                    UploadGame
                                }
                            }
                        }
                    }
                }
            }

            el(HtmlTagName.div) {
                className = ClassName("current-version")

                el(HtmlTagName.span) {
                    textContent = s.version.name
                }

                el(HtmlTagName.span) {
                    className = ClassName("undertale-text-button")

                    textContent = "[Change]"
                    this.clickEvent.addHandler {
                        state = SelectGameVersion
                    }
                }
            }
        }

        UploadGame -> {
            el(HtmlTagName.div) {
                className = ClassName("menu")

                // The native file input can't be styled, so it is hidden inside a label that acts as the button
                el(HtmlTagName.label) {
                    className = ClassName("undertale-button")

                    el(HtmlTagName.div) {
                        textContent = "Select Game Folder"
                    }

                    el(HtmlTagName.input).apply {
                        this.type = InputType.file
                        this.webkitdirectory = true

                        this.changeEvent.addHandler {
                            GlobalScope.launch {
                                detectGame(files!!)
                            }
                        }
                    }
                }
            }
        }

        is ProcessingGame -> {
            processingGame(s.filename, s.count, s.total)
        }

        is DetectedGame -> {
            val detectedVersion = KNOWN_DELTARUNE_VERSIONS[s.hash]

            el(HtmlTagName.div) {
                el(HtmlTagName.div) {
                    className = ClassName("detected-version")

                    el(HtmlTagName.div) {
                        className = ClassName("detected-version-status")

                        // The icon must be a sibling of the text, setting textContent on its parent would remove it
                        compatibilityIcon(getGameCompatibility(s.hash))

                        el(HtmlTagName.div) {
                            if (detectedVersion != null) {
                                className = if (detectedVersion.compatibility == GameCompatibility.COMPATIBLE) ClassName("compatible") else ClassName("incompatible")

                                el(HtmlTagName.span) {
                                    textContent = detectedVersion.displayName
                                }
                            } else {
                                el(HtmlTagName.span) {
                                    className = ClassName("unknown")
                                    textContent = "Unknown game version, it may not work correctly!"
                                }
                            }
                        }
                    }

                    // Shown even for known versions, so users can compare it or report an unknown one
                    el(HtmlTagName.div) {
                        className = ClassName("hash")
                        textContent = "MD5: ${s.hash}"
                    }
                }

                el(HtmlTagName.div) {
                    textContent = "Name"
                }

                val input = el(HtmlTagName.input) {
                    className = ClassName("undertale-text-input")
                    value = detectedVersion?.let { "DELTARUNE ${it.version}" } ?: "DELTARUNE"
                    placeholder = "DELTARUNE"
                }

                val button = el(HtmlTagName.button) {
                    className = ClassName("undertale-button")

                    textContent = "Import"

                    this.clickEvent.addHandler {
                        disabled = true
                        val gameId = Uuid.random().toString()

                        GlobalScope.launch {
                            OPFSUtils.copyFilesToOPFS(
                                "versions/$gameId/bundle",
                                s.files,
                                { name, count ->
                                    state = ProcessingGame(name, count, s.files.size)
                                }
                            )

                            val version = WebruneConfig.GameVersion(gameId, input.value, s.gameWADFile.name)
                            updateConfig { it.copy(versions = it.versions + version, activeVersion = gameId) }

                            navigator.storage.persist()

                            state = Ready(version)
                        }
                    }
                }

                fun updateButtonState() {
                    button.disabled = input.value.isBlank() || detectedVersion?.compatibility == GameCompatibility.INCOMPATIBLE
                }

                updateButtonState()

                input.inputEvent.addHandler {
                    updateButtonState()
                }
            }
        }

        is SelectGameVersion -> {
            el(HtmlTagName.div) {
                className = ClassName("cards-with-header")

                el(HtmlTagName.div) {
                    className = ClassName("card-header")

                    el(HtmlTagName.div) {
                        className = ClassName("card-header-info")

                        el(HtmlTagName.div) {
                            className = ClassName("card-header-title")
                            textContent = "Game Versions"
                        }

                        el(HtmlTagName.div) {
                            className = ClassName("card-header-description")
                            textContent = if (config.versions.size == 1) "1 game version" else "${config.versions.size} game versions"
                        }
                    }

                    el(HtmlTagName.label) {
                        className = ClassName("undertale-button")

                        el(HtmlTagName.div) {
                            textContent = "Add"
                        }

                        el(HtmlTagName.input) {
                            this.type = InputType.file
                            this.webkitdirectory = true

                            this.changeEvent.addHandler {
                                GlobalScope.launch {
                                    detectGame(files!!)
                                }
                            }
                        }
                    }
                }

                for (version in config.versions) {
                    el(HtmlTagName.div) {
                        className = ClassName("game-version")

                        append(version.name)

                        el(HtmlTagName.button) {
                            className = ClassName("undertale-button")

                            textContent = "Select"

                            this.clickEvent.addHandler {
                                GlobalScope.launch {
                                    updateConfig { it.copy(activeVersion = version.id) }
                                    state = Ready(version)
                                }
                            }
                        }
                    }
                }
            }
        }
    }
}

suspend fun loadConfig() {
    config = try {
        val file = navigator.storage.getDirectory().getFileHandle("config.json")
        Json.decodeFromString<WebruneConfig>(file.getFile().byteArray().decodeToString())
    } catch (e: Throwable) {
        WebruneConfig(listOf(), null)
    }
}

suspend fun updateConfig(block: (WebruneConfig) -> WebruneConfig) {
    config = block(config)
    val writable = navigator.storage.getDirectory()
        .getFileHandle("config.json", FileSystemGetFileOptions(create = true))
        .createWritable()
    writable.write(Json.encodeToString(config).encodeToByteArray())
    writable.close()
}

fun main() {
    GlobalScope.launch {
        loadConfig()

        val path = document.location.pathname
        val params = URLSearchParams(window.location.search)

        // This is AWFUL!!!
        if (path == "/play") {
            val gameVersion = config.versions.firstOrNull { it.id == params.get("id") }

            if (gameVersion == null || sessionStorage.getItem("startedFromMainPage") != "true") {
                location.href = "/"
                return@launch
            }

            val gameContainer = document.getElementById(ElementId("game-container"))!!
            gameContainer.replaceChildren()
            val workdir = params.get("workdir") ?: ""

            val iframe = gameContainer.el(HtmlTagName.iframe) {
                src = "/boxed-runner?id=${params.get("id")}&workdir=$workdir"
                allow = "autoplay"
            }

            VirtualGamepad.attachIfTouchDevice(gameContainer, IframeInputSink(iframe))

            window.addEventListener(MessageEvent.MESSAGE, {
                if (it.source != iframe.contentWindow || it.origin != location.origin) return@addEventListener

                val packet = Json.decodeFromString<Boxed2PlayPacket>(it.data as? String ?: return@addEventListener)

                when (packet) {
                    Boxed2PlayPacket.RunnerExit -> location.href = "/"
                    Boxed2PlayPacket.RunnerStarted -> gameContainer.classList.add(ClassName("started"))
                    // The new page needs a click too, so the iframe goes back to taking the entire screen for the "Loading..." and "[CLICK TO START]"
                    Boxed2PlayPacket.RunnerLoading -> gameContainer.classList.remove(ClassName("started"))
                }
            })
        } else if (path == "/boxed-runner") {
            val gameVersion = config.versions.firstOrNull { it.id == params.get("id") }

            if (gameVersion == null || sessionStorage.getItem("startedFromMainPage") != "true") {
                window.parent.postMessage(Json.encodeToString<Boxed2PlayPacket>(Boxed2PlayPacket.RunnerExit))
                return@launch
            }

            // The runner looks for #canvas while booting, so it must already be in the page
            val gameCanvas = document.createElement(HtmlTagName.canvas).apply {
                id = ElementId("canvas")
                width = 640
                height = 480
            }

            val gameContainer = document.getElementById(ElementId("game-container"))!!

            var workdir = params.get("workdir") ?: ""
            if (workdir == "/../")
                workdir = ""

            GlobalScope.launch {
                val rootDirectory = OPFSUtils.getDirectoryNestedHandleCreateIfDoesNotExist("versions/${gameVersion.id}/bundle/", navigator.storage.getDirectory())
                val preloadEntries = mutableListOf<PreloadEntry>()

                val casingMappings = mutableMapOf<String, String>()

                if (workdir.isEmpty()) {
                    // Root game: copy everything except the chapter folders
                    casingMappings += collectPreloadEntries(rootDirectory, "", "", preloadEntries) { it.startsWith("chapter") }
                } else {
                    val workdirPath = workdir.trim('/')
                    val directory = OPFSUtils.getDirectoryNestedHandleCreateIfDoesNotExist("versions/${gameVersion.id}/bundle/$workdirPath/", navigator.storage.getDirectory())
                    casingMappings += collectPreloadEntries(directory, "", "$workdirPath/", preloadEntries) { false }

                    // The shared music folder lives on the bundle root
                    val musDirectory = try {
                        rootDirectory.getDirectoryHandle("mus")
                    } catch (e: Throwable) {
                        null
                    }

                    if (musDirectory != null)
                        casingMappings += collectPreloadEntries(musDirectory, "mus", "mus/", preloadEntries) { false }
                }

                preloadEntries
                    .groupBy { it.gameFileName }
                    .forEach { (_, entries) ->
                        if (entries.size > 1) {
                            println("Duplicated! ${entries.joinToString { it.gameFileName }}")
                        }
                    }

                startYYRunner(
                    gameVersion.id,
                    gameCanvas,
                    workdir,
                    preloadEntries.distinctBy { it.gameFileName }.toTypedArray(),
                    OPFSGameStorage(gameVersion.id, "/_savedata"),
                    { url ->
                        println("Processing URL request $url")

                        // Passthrough http requests
                        // While DELTARUNE doesn't use these... Teiarruma does for its update check
                        if (url.startsWith("http://") || url.startsWith("https://"))
                            return@startYYRunner null

                        if (url == "game.unx") {
                            GlobalScope.promise {
                                OPFSUtils.getFileNestedHandle("versions/${gameVersion.id}/bundle${workdir}/${gameVersion.gameWADName}", navigator.storage.getDirectory())?.getFile()?.arrayBuffer() ?: error("File not found data.win!")
                            }
                        } else {
                            GlobalScope.promise {
                                val patched = resolveURLToOPFSPath(gameVersion, workdir, casingMappings, url)
                                OPFSUtils.getFileNestedHandle(patched, navigator.storage.getDirectory())?.getFile()?.arrayBuffer() ?: error("File not found $patched!")
                            }
                        }
                    },
                    { url ->
                        // http://127.0.0.1:8081/vid/tennaIntroF1_compressed_28.mp4
                        GlobalScope.promise {
                            val patched = resolveURLToOPFSPath(gameVersion, workdir, casingMappings, url)
                            URL.createObjectURL(OPFSUtils.getFileNestedHandle(patched, navigator.storage.getDirectory())?.getFile()!!)
                        }
                    },
                    { workdir, _ ->
                        window.parent.postMessage(Json.encodeToString<Boxed2PlayPacket>(Boxed2PlayPacket.RunnerLoading), location.origin)
                        location.href = "/boxed-runner?id=${gameVersion.id}&workdir=$workdir"
                    },
                    { start ->
                        gameContainer.replaceChildren()

                        // The runner has loaded everything, so only now we ask the player to click
                        gameContainer.clickToStart(gameContainer, gameCanvas) {
                            window.parent.postMessage(Json.encodeToString<Boxed2PlayPacket>(Boxed2PlayPacket.RunnerStarted), location.origin)

                            // You may be wondering "wow this is stupid"
                            // The reason for this is because starting the runner is kinda heavy, so we wait until the next frame to start it so that the "[CLICK TO START]" can disappear
                            requestAnimationFrame {
                                start()
                            }
                        }
                    }
                ) {
                    window.parent.postMessage(Json.encodeToString<Boxed2PlayPacket>(Boxed2PlayPacket.RunnerExit), location.origin)
                }
            }
        } else {
            if (config.versions.isEmpty()) {
                state = UploadGame
            } else {
                state = Ready(config.versions.firstOrNull { it.id == config.activeVersion } ?: config.versions.first())
            }
        }
    }
}

fun resolveURLToOPFSPath(
    gameVersion: WebruneConfig.GameVersion,
    workdir: String,
    casingMappings: Map<String, String>,
    url: String,
): String {
    val filename = url.substringAfterLast("/")
    val properCasing = casingMappings[filename] ?: filename
    println("Proper casing for file $filename is $properCasing")
    // We set properCasing as the missing delimiter value because some URLs may not contain "/"
    // This is an awful, awful hack, we redirect any mus folder to the root folder
    val patched = if (url.startsWith("mus/")) {
        "versions/${gameVersion.id}/bundle/${url.replaceAfterLast("/", properCasing, properCasing)}"
    } else if (url.startsWith(workdir.removePrefix("/") + "/")) {
        "versions/${gameVersion.id}/bundle/${url.replaceAfterLast("/", properCasing, properCasing)}"
    } else {
        // Manually add the workdir if it is missing
        "versions/${gameVersion.id}/bundle$workdir/${url.replaceAfterLast("/", properCasing, properCasing)}"
    }

    println("Converted $url into path $patched")
    return patched
}